import { ServerCommandError, UncertainResultError } from '@/infrastructure/supabase/client'
import type { LocalStore } from '@/platform/localStore'

export type CommandOutcome<T> =
  | { kind: 'ok'; value: T }
  | { kind: 'refused'; code: string; detail?: string }
  | { kind: 'uncertain' }

/**
 * Commande idempotente : la clé est conservée sur la tablette jusqu'à une réponse définitive du serveur. Un résultat
 * incertain (réseau) garde la clé : le prochain essai — même après rechargement — rejoue la même demande, jamais une
 * seconde.
 */
export async function runCommand<T>(
  store: LocalStore,
  scope: string,
  send: (idempotencyKey: string) => Promise<T>,
): Promise<CommandOutcome<T>> {
  let key = await store.getKey(scope)
  if (!key) {
    key = crypto.randomUUID()
    await store.setKey(scope, key)
  }
  try {
    const value = await send(key)
    await store.clearKey(scope)
    return { kind: 'ok', value }
  } catch (error) {
    if (error instanceof ServerCommandError) {
      await store.clearKey(scope)
      return error.detail ? { kind: 'refused', code: error.code, detail: error.detail } : { kind: 'refused', code: error.code }
    }
    if (error instanceof UncertainResultError) return { kind: 'uncertain' }
    // Réponse illisible : traitée comme incertaine, la même clé resservira.
    return { kind: 'uncertain' }
  }
}
